package com.evoapps.evoshift.domain.repository

import com.evoapps.evoshift.domain.model.RequestPlace
import com.evoapps.evoshift.domain.model.WorkRequest
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.flow

/**
 * SH-223: remote work, work events and overtime (`work_requests`), and the workplace's shared
 * places to work from (`workplaces/{id}/work_places`).
 */
interface WorkRequestRepository {
    suspend fun create(request: WorkRequest): Result<String>
    suspend fun update(request: WorkRequest): Result<Unit>
    suspend fun delete(requestId: String): Result<Unit>
    /** The person's own requests at every workplace, newest start first. */
    suspend fun forUser(userId: String): Result<List<WorkRequest>>
    /** The workplace's requests (its managers), newest start first. */
    suspend fun forWorkplace(workplaceId: String): Result<List<WorkRequest>>

    /**
     * SH-224: [userId]'s requests at [workplaceId] that touch [from]..[to] ("YYYY-MM-DD", both
     * included), for the timeline: one query. By default the person's requests, filtered.
     */
    suspend fun forUserBetween(userId: String, workplaceId: String, from: String, to: String): Result<List<WorkRequest>> =
        forUser(userId).map { list -> list.filter { it.workplaceId == workplaceId && it.endDate >= from && it.startDate <= to } }

    /** The workplace's requests, again after every change (the pending badges). By default read once. */
    fun observeForWorkplace(workplaceId: String): Flow<List<WorkRequest>> =
        flow { emit(forWorkplace(workplaceId).getOrDefault(emptyList())) }

    /** The workplace's shared places, by name. */
    suspend fun places(workplaceId: String): Result<List<RequestPlace>>
    suspend fun addPlace(workplaceId: String, place: RequestPlace): Result<RequestPlace>
    /** Managers only (firestore.rules). */
    suspend fun renamePlace(workplaceId: String, placeId: String, name: String): Result<Unit>
    suspend fun removePlace(workplaceId: String, placeId: String): Result<Unit>
}
